import IconFile from "@/components/common/Icons/IconFile";
import {
  Box,
  Button,
  chakra,
  CloseButton,
  Flex,
  FormControl,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";

import TagSelect from "@/components/common/TagSelect";
import { Tag } from "@/types/cloud";
import { formatBytes } from "@/utils/filesize";
import axios from "axios";
import { TFunction, Trans, useTranslation } from "next-i18next";
import useSWR from "swr";
import LimitExceedingModal from "../Payments/LimitExceedingModal";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  parentFolder: number | string;
};

const UploadFilesModal = ({
  isOpen,
  onClose,
  onUpdate,
  parentFolder,
}: Props): React.ReactElement => {
  const fileInput = useRef<HTMLInputElement>();
  const { t } = useTranslation("common");
  const [currentFiles, setCurrentFiles] = useState([]);
  const { data: tags } = useSWR("/tag");
  const [message, setMessage] = useState(null);
  const limitErrorModal = useDisclosure();

  useEffect(() => {
    setCurrentFiles((files) => files.filter((file) => !file.id));
    if (fileInput?.current) fileInput.current.value = "";
    if (!isOpen) onUpdate();
  }, [isOpen]);

  const onDrag = (e) => {
    e.preventDefault();
  };

  const onFileDrop = (e) => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    [...files].forEach((file) => uploadFile(file));
  };

  const onFileChange = (e) => {
    const { files } = e.target;
    [...files].forEach((file) => uploadFile(file));
  };

  const onRemove = (key) => {
    setCurrentFiles((files) =>
      files.filter((file) => {
        if (file.key !== key) return true;
        if (file?.id)
          axios.delete(`/cloud/file/${file.id}`).then(() => onUpdate());
        else file?.request?.cancel();

        return false;
      })
    );
  };

  const uploadFile = (currentFile: File) => {
    const formData = new FormData();
    const uniqueId = Math.floor(Math.random() * Date.now());
    const request = axios.CancelToken.source();

    setCurrentFiles((files) => [
      ...files,
      {
        name: currentFile.name,
        size: currentFile.size,
        key: uniqueId,
        progress: 0,
        tags: [],
        request,
      },
    ]);
    formData.append("parentFolder", `${parentFolder}`);
    formData.append("file", currentFile);
    axios
      .post("cloud/file", formData, {
        cancelToken: request.token,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (data) => {
          const progress = Math.round((100 * data.loaded) / data.total);
          setCurrentFiles((files) =>
            files?.map((file) =>
              file.key === uniqueId
                ? {
                    ...file,
                    progress,
                  }
                : file
            )
          );
        },
      })
      .then((res) => {
        onUpdate();

        setCurrentFiles((files) =>
          files?.map((file) => {
            if (file.key !== uniqueId) return file;
            if (file.tags.length) axios.post(`cloud/file/${file.id}`, { tags });
            return { ...file, id: res.data.id };
          })
        );
      })
      .catch((error) => {
        if (error == "CloudSpaceFullLimit") {
          setMessage(error);
          limitErrorModal.onOpen();
        } else {
          onClose();
        }
      });
  };

  const updateTags = (key, tags) => {
    setCurrentFiles((files) =>
      files?.map((file) => {
        if (file.key !== key || !file.id) return file;
        axios.post(`cloud/file/${file.id}`, { tags });
        return { ...file, tags };
      })
    );
  };

  const hasFiles = currentFiles.length > 0;

  const isSubmitting = useMemo(
    () =>
      currentFiles.reduce(
        (totalProgress, file) => totalProgress + file?.progress,
        0
      ) <
      currentFiles.length * 100,
    [currentFiles]
  );

  return (
    <>
      {limitErrorModal.isOpen && (
        <LimitExceedingModal
          errorMessage={message}
          isOpen
          onClose={limitErrorModal.onClose}
        />
      )}

      <Modal
        motionPreset="slideInBottom"
        isOpen={isOpen}
        onClose={onClose}
        preserveScrollBarGap
        isCentered
        scrollBehavior="inside"
      >
        <input
          hidden
          multiple
          id="file-upload"
          name="file-upload"
          type="file"
          ref={fileInput}
          onChange={onFileChange}
        />
        <ModalOverlay>
          <ModalContent
            borderRadius=".375rem"
            maxWidth="900px"
            maxH="100vh"
            overflow="auto"
          >
            <ModalHeader borderBottomColor="light.50" borderBottomWidth="1px">
              <Text fontSize="1rem" fontWeight="500" mb={3}>
                {t("cloud.uploadFiles")}
              </Text>
              <Flex
                justify="center"
                px={6}
                pt={5}
                pb={6}
                borderWidth={2}
                borderColor="light.300"
                borderStyle="dashed"
                rounded="md"
                minH="200px"
                align="center"
                onDrop={onFileDrop}
                onDragOver={onDrag}
                onDragEnter={onDrag}
                onDragLeave={onDrag}
              >
                <Stack spacing={1} textAlign="center">
                  <Icon
                    mx="auto"
                    boxSize={12}
                    color="text.500"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Icon>
                  <Flex fontSize="sm" color="text.500" alignItems="baseline">
                    <Trans i18nKey="cloud.dragAndDrop">
                      <chakra.label
                        htmlFor="file-upload"
                        cursor="pointer"
                        rounded="md"
                        fontSize="md"
                        color="main.800"
                        pos="relative"
                        _hover={{
                          color: "main.900",
                        }}
                      >
                        <span>{t("chooseFile")}</span>
                      </chakra.label>
                      <Text pl={1}>{t("orDragAndDrop")}</Text>
                    </Trans>
                  </Flex>
                </Stack>
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {hasFiles && (
                <VStack spacing="3">
                  {currentFiles?.map((file) => (
                    <File
                      t={t}
                      id={file?.key}
                      key={file?.key}
                      name={file.name}
                      size={file.size}
                      progress={file.progress}
                      remove={onRemove}
                      tagsOptions={tags}
                      tags={file.tags}
                      updateTags={updateTags}
                    />
                  ))}
                </VStack>
              )}
            </ModalBody>

            {hasFiles && (
              <ModalFooter>
                <Button isLoading={isSubmitting} onClick={onClose}>
                  {t("done")}
                </Button>
              </ModalFooter>
            )}
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  );
};

type FileProps = {
  id?: number;
  t: TFunction;
  name: string;
  size: number;
  progress?: number;
  remove: (key) => void;
  tagsOptions: Tag[];
  tags: { id: number }[];
  updateTags: (key, tags) => void;
};

const File = ({
  t,
  id,
  remove,
  name,
  size,
  progress,
  tagsOptions,
  tags,
  updateTags,
}: FileProps) => (
  <Box w="100%">
    <Flex align="center" mb={2}>
      <Box w="55px">
        <IconFile />
      </Box>
      <Box flex="1" maxW="calc(100% - 55px)">
        <Flex align="center" maxW="100%">
          <Box flex="1" maxW="calc(100% - 108px)">
            <Text maxW="100%" fontWeight="500" isTruncated>
              {name}
            </Text>
          </Box>
          <Text fontSize="xs" color="text.500" textAlign="right" w="80px">
            {formatBytes(size)}
          </Text>
          <CloseButton
            onClick={() => remove(id)}
            w="20px"
            h="20px"
            fontSize="10px"
            ml={2}
          />
        </Flex>
        <Progress value={progress || 0} mt={2} />
      </Box>
    </Flex>
    <TagSelect
      options={tagsOptions}
      placeholder={t("cloud.chooseOrTypeTags")}
      value={tags?.map((tag) => tag.id)}
      onChange={(values) =>
        updateTags(
          id,
          values?.map((value) => ({
            id: value.value,
          }))
        )
      }
      isClearable={true}
    />
  </Box>
);

export default UploadFilesModal;
